/**
 * turnIndicator — ONE derived truth for the pane's in-flight turn indicator.
 *
 * INCIDENT (owner report 2026-08-12, connect-chip send): the rail row showed
 * a live spinner + elapsed clock (`processingByThread`) while the thread body
 * showed NOTHING — the direct-connect pre-route retired the turn's only
 * pending widget and then awaited a network-bound tool that posts no pending
 * of its own. Fourth incident of the same divergence (2026-07-13 blank pane,
 * 2026-07-14 flag-release blank, the planActiveTaskReattach gap, and this):
 * the rail derives "turn in flight" from the per-thread processing flag while
 * the body derives it from the PRESENCE of a pending-shaped message — two
 * sources of truth, no reconciliation, so either can be wrong alone.
 *
 * This module makes the body's half structurally safe:
 *   1. `isActivePendingShapedMessage` — SHAPE-based detection (the animated
 *      widget html) replaces the renderer's hardcoded sourceTag list, so
 *      every pending-shaped row is extracted into the single overlay lane and
 *      none can render as a second inline spinner (`prest0n_tool_start_*`,
 *      payload-render placeholders — the two-indicator class).
 *   2. `resolveSyntheticTurnIndicator` — when the processing flag says a turn
 *      is in flight, the assistant has produced nothing yet (newest visible
 *      message is the user's), and NO pending-shaped message exists, the pane
 *      renders a synthetic widget. The exactly-one invariant then holds even
 *      when a handler forgot (or retired) its widget — the failure mode
 *      becomes "generic label" instead of "no indicator at all".
 *
 * Deliberately NOT changed: the rail keeps its own flag-driven spinner
 * (owner override 2026-08-01 — pane + row clocks coexist by design), and the
 * overlay's newer-content suppression stays (a streaming reply IS the
 * indicator; pinning a spinner next to it re-opens the 2026-07-14 class).
 *
 * FIFTH incident of the divergence (owner report 2026-08-17, unread-email
 * digest): a multi-step task posted a SETTLED interim note ("the full digest
 * will follow in this thread") and kept working — the newest visible message
 * was then the assistant's, the synthetic fallback bailed on `isUser !== true`,
 * and the pane went blank for the remaining minutes while the rail clock ran.
 * Fix: the synthetic now yields only to a LIVE reply (status 'streaming' or a
 * still-bodyless placeholder — the in-flight shapes stamped by useGlobalChat),
 * not to any assistant row. A bodied, non-streaming assistant note is settled
 * content, and settled content is not an indicator. Known benign edge: a
 * second tab syncing a mid-stream row from Firestore (status undefined, body
 * growing) may briefly show the widget under the growing reply — strictly
 * better than the blank pane, and it clears at finalize.
 */

import type { ChatMessage } from './types';
import { getPendingWidgetHtml } from '../../services/globalTools/widgetHtml';

export const SYNTHETIC_TURN_INDICATOR_SOURCE_TAG = 'synthetic_turn_indicator';

/** Default label for the synthetic fallback widget (no clock — a frozen
 *  elapsed readout looks broken; the rail row owns the live clock). */
export const SYNTHETIC_TURN_INDICATOR_LABEL = 'working on it';

/** Label when the assistant already posted an interim note and kept going —
 *  "working on it" after a visible reply reads like a stutter. */
export const SYNTHETIC_TURN_INDICATOR_CONTINUED_LABEL = 'still working';

/**
 * A live in-flight reply — the shapes stamped by useGlobalChat while content
 * is actually arriving: explicit streaming status, or a placeholder with no
 * body yet. These ARE the indicator; the synthetic must yield to them.
 * A bodied assistant row without streaming status is SETTLED content.
 */
export function isLiveInFlightReply(m: ChatMessage | null | undefined): boolean {
  if (!m || m.isUser) return false;
  if (m.status === 'streaming') return true;
  const hasBody = !!((m.text || '').trim() || (m.html || '').trim());
  return !hasBody;
}

/**
 * Upper bound on html that can still be "just the widget". A pure pending
 * widget (spinner pill), even stacked with a section-progress banner and the
 * streaming-tail affordance, stays comfortably under this; a streamed content
 * preview blows past it within its first paragraphs.
 */
export const PENDING_WIDGET_MAX_HTML_CHARS = 4096;

/**
 * Active pending-shaped content: the animated widget html, not yet finalized.
 * Stopped widgets are terminal content (render inline), and a pending-shaped
 * message CARRYING attachments is content the user must see — never extracted.
 *
 * CONTENT-BEARING STREAMS ARE NEVER EXTRACTED (2026-08-22, the translation
 * squish): this used to be a bare substring test, and the translate tool's
 * streaming message — progress banner + the WHOLE accumulated document
 * preview + the animated tail — always contains the animation names via the
 * tail widget. The entire preview was pulled out of the scrollable transcript
 * into the pending-overlay lane below it, a row with no height bound inside a
 * height-capped overflow:hidden pane: the preview grew, and the conversation
 * collapsed to a sliver pinned at the top. A message whose html carries a
 * content block (<article — every formatted preview wraps in one) or is
 * simply too large to be a widget renders INLINE, where the pinned-to-bottom
 * transcript controller keeps its growing edge visible like any other stream.
 */
export function isActivePendingShapedMessage(m: ChatMessage | null | undefined): boolean {
  if (!m || m.isUser) return false;
  const html = m.html || '';
  if (!html.includes('pendingShimmer') && !html.includes('pendingSpin')) return false;
  if (html.includes('stopped-widget-wrapper')) return false;
  if (Array.isArray(m.attachments) && m.attachments.length > 0) return false;
  if (html.includes('<article')) return false;
  if (html.length > PENDING_WIDGET_MAX_HTML_CHARS) return false;
  return true;
}

/**
 * The synthetic fallback indicator, or null when the invariant already holds.
 *
 * Fires ONLY when all three are true:
 *   • the active thread's processing flag is set (a turn is genuinely in
 *     flight — the same truth the rail spinner renders),
 *   • no pending-shaped message exists anywhere in the transcript (nothing
 *     for the overlay lane to show), and
 *   • the newest visible message is NOT a live in-flight reply — the user's
 *     message, or a SETTLED assistant note (an interim update posted mid-task).
 *     Only an actively-streaming/bodyless reply suppresses the synthetic,
 *     because only that content is itself an indicator (2026-08-17 fix).
 */
export function resolveSyntheticTurnIndicator(params: {
  processing: boolean;
  messages: ReadonlyArray<ChatMessage> | undefined;
}): ChatMessage | null {
  const { processing, messages } = params;
  if (!processing) return null;
  if (!Array.isArray(messages) || messages.length === 0) return null;
  let newestVisible: ChatMessage | null = null;
  for (let i = messages.length - 1; i >= 0 && !newestVisible; i -= 1) {
    const m = messages[i];
    if (!m || m.isContextOnly) continue;
    newestVisible = m;
  }
  if (!newestVisible) return null;
  // A pending-shaped NEWEST row is a live widget (the extraction lane owns
  // it) — never double-render. Pending-shaped rows EARLIER than newer real
  // content are stale widgets the renderer already suppressed; they must not
  // veto the synthetic (that veto was the 2026-08-17 blank-pane sub-case).
  if (isActivePendingShapedMessage(newestVisible)) return null;
  if (isLiveInFlightReply(newestVisible)) return null;
  const label = newestVisible.isUser === true
    ? SYNTHETIC_TURN_INDICATOR_LABEL
    : SYNTHETIC_TURN_INDICATOR_CONTINUED_LABEL;
  return {
    id: SYNTHETIC_TURN_INDICATOR_SOURCE_TAG,
    isUser: false,
    text: `${label}...`,
    html: getPendingWidgetHtml(label),
    sourceTag: SYNTHETIC_TURN_INDICATOR_SOURCE_TAG,
    timestamp: new Date(),
  } as ChatMessage;
}

// ────────────────────────────────────────────────────────────────────────────
// Stuck-flag sweep (owner report 2026-08-19, "still working" for 30+ minutes)
// ────────────────────────────────────────────────────────────────────────────
//
// SIXTH incident of the flag/transcript divergence, and the worst: two
// threads' turns both completed and delivered their replies (~09:00 PM), yet
// both rail clocks ran past 31 minutes and both panes kept rendering the
// synthetic "still working" widget. Two structural holes let a leaked flag
// live forever:
//
//   1. The 10-minute safety floor is RENDER-DRIVEN — it lives in a computed
//      whose deps are state fields. A stuck flag with no further state churn
//      never re-evaluates it, so the "floor" simply never runs. Time-based
//      invariants need a timer.
//   2. There was no POSITIVE-terminal reconciliation at all: when the session
//      per-turn ledger (usePrest0nSession, 2026-08-16) has already recorded
//      the thread's turn as TERMINAL, the flag has no living owner — waiting
//      out a 10-minute floor for a provably-finished turn is 10 minutes of
//      lying to the user.
//
// planStuckProcessingClears is the pure decision half: given each flagged
// thread's heartbeat stamp, its session-ledger turn, and its transcript, it
// returns the flags that must clear NOW and why. The host runs it on a
// timer (plus on every ledger mutation) and dispatches the same thread-keyed
// stopProcessing every legitimate finalize path uses.
//
// Deliberately conservative:
//   • 'terminal-turn-settled' requires POSITIVE evidence on every axis: a
//     ledger entry in phase 'terminal' past a grace window, a transcript with
//     NO active pending-shaped widget, and a newest visible message that is
//     not a live in-flight reply. A live/absent ledger entry never clears —
//     the 2026-08-17 interim-note class (settled note, turn still working)
//     stays protected because its turn is still live in the ledger.
//   • 'heartbeat-floor' mirrors the existing PROCESSING_SAFETY_TIMEOUT_MS
//     semantics exactly (stale per-thread heartbeat stamp) — it just runs on
//     a clock instead of hoping for a re-render.

export type StuckProcessingClearReason = 'terminal-turn-settled' | 'heartbeat-floor';

/** Grace after a ledger-terminal turn before its flag is force-cleared.
 *  Long enough for every legitimate finalize path (and any short wrap-up
 *  leg) to clear the flag itself; short enough that the user never watches
 *  a phantom spinner over a delivered reply for long. */
export const TERMINAL_TURN_CLEAR_GRACE_MS = 60_000;

/** Mirrors useGlobalChat's PROCESSING_SAFETY_TIMEOUT_MS (10 min). */
export const STUCK_FLAG_HEARTBEAT_FLOOR_MS = 10 * 60 * 1000;

export interface StuckProcessingSweepThread {
  threadId: string;
  /** processingStartedAtByThread[threadId] — heartbeat-refreshed stamp. */
  lastHeartbeatAtMs: number | null | undefined;
  /** Session ledger phase for the thread (getTurnForThread), null if untracked. */
  turnPhase: 'streaming' | 'polling' | 'terminal' | null;
  /** endedAtMs of the ledger entry (meaningful when turnPhase === 'terminal'). */
  turnEndedAtMs: number | null | undefined;
  /** The thread's transcript (rendered state for the active thread, the
   *  per-thread store otherwise). undefined ⇒ transcript unavailable — the
   *  terminal rule is skipped (never clear without seeing the pane). */
  messages: ReadonlyArray<ChatMessage> | undefined;
  /** Server-confirmed pending/running work for this thread (active-task
   *  discovery — discoveryLitThreadsRef). The 2026-08-23 audit-thread class:
   *  each turn terminalizes on the client while delegated subtasks keep
   *  running server-side; rule 1's "no living owner" premise is FALSE then,
   *  so a true value vetoes the terminal clear. The heartbeat floor still
   *  applies — discovery re-arms refresh the heartbeat while work is truly
   *  live, and a dead flag ages out on rule 2 as before. */
  serverConfirmedActive?: boolean;
}

export interface StuckProcessingClear {
  threadId: string;
  reason: StuckProcessingClearReason;
}

export function planStuckProcessingClears(
  threads: ReadonlyArray<StuckProcessingSweepThread>,
  nowMs: number,
): StuckProcessingClear[] {
  const clears: StuckProcessingClear[] = [];
  for (const t of threads) {
    if (!t || !t.threadId) continue;

    // Rule 1 — positive terminal evidence (fast path, ~1 min). Vetoed
    // outright by server-confirmed active work: a terminal CLIENT turn with
    // live server-side tasks (delegated subtasks, async continuations) is
    // exactly the background-work case the widget exists for.
    if (t.turnPhase === 'terminal' && t.serverConfirmedActive !== true) {
      const endedAt = typeof t.turnEndedAtMs === 'number' ? t.turnEndedAtMs : 0;
      const pastGrace = nowMs - endedAt > TERMINAL_TURN_CLEAR_GRACE_MS;
      if (pastGrace && Array.isArray(t.messages) && t.messages.length > 0) {
        const anyPendingWidget = t.messages.some((m) => isActivePendingShapedMessage(m));
        let newestVisible: ChatMessage | null = null;
        for (let i = t.messages.length - 1; i >= 0 && !newestVisible; i -= 1) {
          const m = t.messages[i];
          if (!m || m.isContextOnly) continue;
          newestVisible = m;
        }
        const newestIsLive = isLiveInFlightReply(newestVisible);
        if (!anyPendingWidget && newestVisible && !newestIsLive) {
          clears.push({ threadId: t.threadId, reason: 'terminal-turn-settled' });
          continue;
        }
      }
    }

    // Rule 2 — stale heartbeat floor (slow path, 10 min; same threshold the
    // render-driven floor uses, now guaranteed to actually run).
    if (
      typeof t.lastHeartbeatAtMs === 'number' &&
      t.lastHeartbeatAtMs > 0 &&
      nowMs - t.lastHeartbeatAtMs > STUCK_FLAG_HEARTBEAT_FLOOR_MS
    ) {
      clears.push({ threadId: t.threadId, reason: 'heartbeat-floor' });
    }
  }
  return clears;
}
